import { Link, useLocation, useNavigate } from 'react-router'
import { WORKSPACE_SHELL_ENABLED } from '../../lib/feature-flags.js'
import { IconClose16, IconHouse16 } from '../icons/index.js'
import {
  previousTabAfterClosing,
  useOpenCampaignTabs,
  type OpenCampaignTab,
} from './useOpenCampaignTabs.js'

const CAMPAIGN_ID_IN_PATH = /^\/campaigns\/([^/]+)/

/**
 * TabStrip — Epic 48 Phase 02C, `DESIGN-HANDOVER.md` §8.2. A 40px browser-tab-style strip:
 * a 40×40 house icon, then one auto-width tab per open campaign, edge-to-edge. Evidenced on
 * nodes `133:15900`, `150:19613`, `183:24140`, `150:18614`, `133:14892` as sitting above every
 * screen in the product — the global sidebar, the campaign list, and every per-campaign
 * workspace screen.
 *
 * PLAN.md's `campaign-tabs-strip` item is `accepted` but CONDITIONED: only the structure and
 * the session model (this file + `useOpenCampaignTabs.ts`) are accepted. Two things this
 * component deliberately does NOT do, both recorded as open in `DESIGN-HANDOVER.md` §10 item
 * 16/24 and phase task 2C.6 — do not "fix" either without a `/design-change-review`:
 *
 *   1. No keyboard/ARIA tablist model. No `role="tablist"`/`"tab"`, no `aria-selected`, no
 *      arrow-key focus path, no keyboard close/reorder shortcut. Tabs are plain links and the
 *      close control is a plain button — both natively focusable and Enter/Space-operable, so
 *      the strip has a correct baseline without inventing the richer model that's still open.
 *   2. No per-tab max-width or truncation. §10 item 24 (narrowed by D-TABS-1) leaves this
 *      open; tabs stay auto-width per the evidenced geometry (145–196px, driven by campaign
 *      name length) and the strip scrolls horizontally instead (2C.4).
 *
 * MEASURED AGAINST NODE `133:16380` (2026-08-29) after the owner reported the strip "has nothing
 * to do with the design". It did not — the implementation had INVERTED it. The node says: the strip
 * is WHITE with a 1px `#e6e6e6` border; the ONLY grey is the home cell, `#ececec`
 * (`surface/selected/default`) on 12px padding; each tab carries its own 1px `#e6e6e6` border with
 * `px-12 py-6`, `min-h-24`, no fill; text is Inter **Medium 12px/15px**; and selection is signalled
 * by TEXT COLOUR ALONE — `#1a1a1a` selected against `#4d4d4d` unselected. What shipped instead put
 * `surface-tertiary` grey on the strip AND every tab, gave the active tab a `#ececec` FILL, left
 * the home cell unfilled, and used no tab borders at all.
 *
 * ⚠ THE ACTIVE TREATMENT ABOVE IS SUPERSEDED (2026-08-31). That paragraph is kept because it is
 * still true OF `133:16380`, and because the correction is easier to trust next to what it
 * corrects — but the tab bar was redrawn as `Main Tab Buttons` (`411:*`), and the new active
 * state is not "text colour alone". Read from the node's own variables:
 *   ACTIVE  (`411:5047`) — surface `Colors/Surface/Selected/Default` #ececec,
 *                          text `Colors/Text/Selected/Default` #1a1a1a, weight Semi Bold (600).
 *   INACTIVE(`411:5043`) — surface `Colors/Surface/Default/White` #ffffff,
 *                          text `Colors/Text/Unselected/Default` #4d4d4d, weight Medium (500).
 * So the `#ececec` fill the 2026-08-29 correction REMOVED is now correct, on a different node —
 * which is exactly why this reads the variables rather than an earlier decision about them.
 *
 * ⚠ TOKEN GAP, recorded not invented: the node's selected text colour is Figma variable
 * `text/selected/default` = `#1a1a1a`, and `index.css` has no text token for it — only
 * `--color-icon-tabs-selected`, same value, labelled for icons and marked "Tabs only, achromatic
 * exception". This file reuses that token for the text because the measured value is identical and
 * the exception is the same one; adding `--color-text-tabs-selected` would be inventing a token to
 * fix a naming problem. The gap belongs in §10 alongside open question 29.
 *
 * MOUNTING (updated — Epic 48 Phase 02C lift, 2026-08-29): this now mounts ONCE, in
 * `WorkspaceAppShell.tsx`, ABOVE the sidebar+content row, with no `current` — the shell wraps
 * every route (`/dashboard`, `/brands`, `/trash`, `/admin`, `/campaigns/:id`) and has no
 * campaign of its own. `WorkspacePage` (the one screen that DOES know the campaign) no longer
 * mounts this component at all; it registers its campaign into the same shared store via
 * `useRegisterCampaignTab` (`useOpenCampaignTabs.ts`) instead, so the strip mounted in the
 * shell picks it up. See that file's header for why this needed a module-level store rather
 * than the `useState` this hook used before the lift.
 *
 * Historical note (Phase 02C, when this file was first built): the strip was its own mount
 * point, not yet a child of any shell — the phase's own note at the time was that the strip
 * sits above the global sidebar (§8.1) that a *different* phase builds, so the two had to be
 * able to land and be composed independently before either could be wired together. `current`
 * used to be supplied by `WorkspacePage` mounting this component directly.
 */
export interface TabStripProps {
  /**
   * The campaign the mounting screen is presently showing, if any. Passing one is what opens
   * its tab (D-TABS-1 — there is no separate open gesture). Omit / pass `null` on a screen
   * that isn't scoped to a single campaign (e.g. the campaign list) — the strip still renders
   * whatever tabs are already open, just with none of them marked active.
   */
  current?: OpenCampaignTab | null
  /** Overridable for tests; defaults to the real D-CUTOVER-1 coexistence flag (2C.5). */
  enabled?: boolean
  className?: string
}

export function TabStrip({ current, enabled = WORKSPACE_SHELL_ENABLED, className }: TabStripProps) {
  const location = useLocation()
  const { tabs, closeTab } = useOpenCampaignTabs(current)
  const navigate = useNavigate()

  // 2C.5: flag off → this mount point renders nothing, today's chrome is unchanged. Hooks
  // above still run unconditionally (rules-of-hooks) — they're cheap, and keeping tab state
  // warm means a later flag flip doesn't need this component to "catch up."
  if (!enabled) return null

  const activeId = location.pathname.match(CAMPAIGN_ID_IN_PATH)?.[1] ?? null

  return (
    <div
      data-tab-strip
      className={`flex h-10 flex-none items-stretch border border-border-primary bg-surface-white${
        className ? ` ${className}` : ''
      }`}
    >
      {/* The home cell is the ONLY grey in the strip — `surface/selected/default` #ececec on a
          40×40 cell (12px padding around a 16px icon). Until 2026-08-29 this was inverted: the
          strip and every tab were grey and the home cell had no fill at all. */}
      <Link
        to="/campaigns"
        aria-label="Campaigns"
        className="flex flex-none items-center justify-center bg-surface-tabs-selected p-large text-icon-tabs-selected"
      >
        <IconHouse16 />
      </Link>
      {/* `nav`, not `tablist` — see the file header. `aria-label` is the "correct basic
          accessible name" the phase asks for; it is not a stand-in for the still-open
          tablist/aria-selected model. */}
      <nav
        aria-label="Open campaigns"
        className="flex min-w-0 flex-1 items-stretch overflow-x-auto"
      >
        {tabs.map((tab) => {
          const active = tab.id === activeId
          return (
            <div
              key={tab.id}
              data-tab-active={active ? 'true' : 'false'}
              className={`flex min-h-6 flex-none items-center justify-center gap-small whitespace-nowrap border border-border-primary px-large py-small-medium ${
                active
                  ? 'bg-surface-tabs-selected text-body-default-strong text-icon-tabs-selected'
                  : 'bg-surface-white text-body-default-medium text-text-secondary'
              }`}
            >
              <Link to={`/campaigns/${tab.id}`}>{tab.name}</Link>
              {/* THE CLOSE (`×`), BACK ON 2026-08-31 — and the whole point is that it is no longer
                  an invention. It was removed 2026-08-29 on the owner's instruction ("the top tab
                  parts are not in the design; stay away from inventions and stick to Figma"), and
                  that removal recorded its own cost in plain words: there was then NO WAY for a
                  user to close a tab by hand, which this file called the Figma's own gap rather
                  than papering over it. The design has since closed that gap: `Main Tab Buttons`
                  (`411:5047`) draws a 24×24 `Close Button` holding `icon / 16 / Close`, and it
                  draws it ONLY on the active tab — the inactive instance (`411:5043`) has a name
                  and nothing else, and its name is even allowed to be wider (116 vs 88) because
                  nothing shares the row. So: active only, exactly as drawn.

                  `closeTab` was deliberately kept alive in `useOpenCampaignTabs` through the whole
                  removal ("it is tested and costs nothing dormant, so whatever affordance a design
                  round settles on can use it"). This is that affordance.

                  A SIBLING of the `Link`, never nested inside it — the same invalid-HTML /
                  unreliable-a11y rule `DimensionListRegion`'s row kebab already follows. */}
              {active && (
                <button
                  type="button"
                  aria-label={`“${tab.name}” sekmesini kapat`}
                  onClick={() => {
                    // Back to the tab you were on BEFORE — Chrome's rule, and the owner's
                    // answer to the one thing the design does not draw (2026-08-31). My first
                    // guess took the first tab in DISPLAY order, which has nothing to do with
                    // where the user had been. `previousTabAfterClosing` reads the recency the
                    // store already keeps. Computed before `closeTab` mutates the set.
                    const previous = previousTabAfterClosing(tab.id)
                    closeTab(tab.id)
                    void navigate(previous ? `/campaigns/${previous.id}` : '/campaigns')
                  }}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-100 text-icon-secondary hover:text-icon-default"
                >
                  <IconClose16 />
                </button>
              )}
            </div>
          )
        })}
      </nav>
    </div>
  )
}
